import {
  type BooleanInput,
  coerceBooleanProperty,
  coerceNumberProperty,
  type NumberInput,
} from '@angular/cdk/coercion';
import {
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
  VirtualScrollStrategy,
} from '@angular/cdk/scrolling';
import { Directive, forwardRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';

import {
  BLAZE_SCROLL_OFFSET_ADAPTER,
  ScrollOffsetAdapter,
} from '../scroll-offset-adapter/scroll-offset-adapter';

import { BlazeAutosizeStrategy } from './autosize-strategy';
import { BlazeFixedSizeStrategy } from './fixed-size-strategy';
import { VIRTUAL_SCROLL_NODES_PROVIDER, VirtualScrollNodesProvider } from './item-index-provider';
import { ItemSizePredictor, VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR } from './item-size-predictor';

export class BlazeFlexibleVirtualScrollStrategy<T> implements VirtualScrollStrategy {
  private readonly scrolledIndexChange$$ = new Subject<number>();
  private viewport: CdkVirtualScrollViewport | null = null;
  private enableAutosize: boolean;
  private readonly fixedSizeStrategy: BlazeFixedSizeStrategy;
  private readonly autosizeStrategy: BlazeAutosizeStrategy<T>;

  scrolledIndexChange: Observable<number> = this.scrolledIndexChange$$.pipe(distinctUntilChanged());

  private get strategy(): BlazeFixedSizeStrategy | BlazeAutosizeStrategy<T> {
    return this.enableAutosize ? this.autosizeStrategy : this.fixedSizeStrategy;
  }

  constructor(
    itemSize: number,
    itemsBufferCount: number,
    minBufferPx: number,
    maxBufferPx: number,
    enableAutosize: boolean,
    itemSizePredictor: ItemSizePredictor<T>,
    autosizeNodesProvider: VirtualScrollNodesProvider,
    scrollAdapter: ScrollOffsetAdapter,
  ) {
    this.enableAutosize = enableAutosize;
    this.fixedSizeStrategy = new BlazeFixedSizeStrategy(
      this.scrolledIndexChange$$,
      scrollAdapter,
      itemSize,
      minBufferPx,
      maxBufferPx,
    );
    this.autosizeStrategy = new BlazeAutosizeStrategy<T>(
      itemSizePredictor,
      this.scrolledIndexChange$$,
      autosizeNodesProvider,
      scrollAdapter,
      itemsBufferCount,
      null,
    );
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.strategy.attach(viewport);
  }

  detach(): void {
    this.strategy.detach();
  }

  updateItemAndBufferSize(itemSize: number, minBufferPx: number, maxBufferPx: number): void {
    this.fixedSizeStrategy.updateItemAndBufferSize(itemSize, minBufferPx, maxBufferPx);
  }

  toggleAutosize(autosize: boolean): void {
    this.detach();
    this.enableAutosize = autosize;

    if (!this.viewport) {
      return;
    }

    this.attach(this.viewport);
  }

  resetItemSizeCache(): void {
    this.autosizeStrategy.resetSizeCache(this.enableAutosize ? this.viewport : null);
  }

  onContentScrolled(): void {
    if (!this.viewport) {
      return;
    }

    this.strategy.updateRenderedRange(this.viewport);
  }

  onDataLengthChanged(): void {
    if (!this.viewport) {
      return;
    }

    this.strategy.onDataLengthChanged(this.viewport);
  }

  onContentRendered(): void {
    /* empty */
  }

  onRenderedOffsetChanged(): void {
    /* empty */
  }

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (!this.viewport) {
      return;
    }

    this.strategy.scrollToIndex(this.viewport, index, behavior);
  }

  updateItems(items: any[]): void {
    this.autosizeStrategy.updateItems(items, this.viewport);
  }

  updateItemsBufferCount(value: number): void {
    this.autosizeStrategy.updateItemsBufferCount(value, this.viewport);
  }

  updateItemSize(index: number, value: number): void {
    if (this.viewport && this.strategy instanceof BlazeAutosizeStrategy) {
      this.strategy.updateItemSize(this.viewport, index, value);
    }
  }
}

@Directive({
  selector: '[blazeRowAutosize]',
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (
        directive: RowAutosizeVirtualScrollDirective<any>,
      ): BlazeFlexibleVirtualScrollStrategy<any> => directive.scrollStrategy,
      deps: [forwardRef(() => RowAutosizeVirtualScrollDirective<any>)],
    },
  ],
  standalone: false,
})
export class RowAutosizeVirtualScrollDirective<T> implements OnChanges {
  private readonly predictor = inject<ItemSizePredictor<T>>(VIRTUAL_SCROLL_ITEM_SIZE_PREDICTOR);
  private readonly htmlElementsProvider = inject<VirtualScrollNodesProvider>(
    VIRTUAL_SCROLL_NODES_PROVIDER,
  );
  private readonly scrollAdapter = inject<ScrollOffsetAdapter>(BLAZE_SCROLL_OFFSET_ADAPTER);

  @Input() rowDOMSelector = '.row';
  @Input() hostDOMSelector = '.table-body';

  private _itemSize = 50;
  private _minBufferPx = 100;
  private _maxBufferPx = 200;
  private _itemsBufferCount = 3;
  private _items: T[] = [];
  private _enableAutosize = false;

  scrollStrategy = new BlazeFlexibleVirtualScrollStrategy<T>(
    this.itemSize,
    this.autosizeItemsBufferCount,
    this.minBufferPx,
    this.maxBufferPx,
    this.enableAutosize,
    this.predictor,
    this.htmlElementsProvider,
    this.scrollAdapter,
  );

  @Input() set itemSize(value: NumberInput) {
    this._itemSize = coerceNumberProperty(value);
  }
  get itemSize(): number {
    return this._itemSize;
  }

  @Input() set minBufferPx(value: NumberInput) {
    this._minBufferPx = coerceNumberProperty(value);
  }
  get minBufferPx(): number {
    return this._minBufferPx;
  }

  @Input() set maxBufferPx(value: NumberInput) {
    this._maxBufferPx = coerceNumberProperty(value);
  }
  get maxBufferPx(): number {
    return this._maxBufferPx;
  }

  @Input() set autosizeItemsBufferCount(value: number) {
    this._itemsBufferCount = coerceNumberProperty(value);
    this.scrollStrategy.updateItemsBufferCount(this._itemsBufferCount);
  }
  get autosizeItemsBufferCount(): number {
    return this._itemsBufferCount;
  }

  @Input() set items(value: T[] | null) {
    if (value && this._items.length !== value.length) {
      this.scrollStrategy.updateItems(value);
      this._items = value;
    }
  }
  get items(): T[] {
    return this._items;
  }

  @Input() set enableAutosize(value: BooleanInput) {
    this._enableAutosize = coerceBooleanProperty(value);
    this.toggleAutosize();
  }
  get enableAutosize(): boolean {
    return this._enableAutosize;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemSize'] || changes['minBufferPx'] || changes['maxBufferPx']) {
      this.scrollStrategy.updateItemAndBufferSize(
        this.itemSize,
        this.minBufferPx,
        this.maxBufferPx,
      );
    }
  }

  toggleAutosize(autosize = this.enableAutosize): void {
    this.scrollStrategy.toggleAutosize(autosize);
  }

  resetItemSizeCache(): void {
    this.scrollStrategy.resetItemSizeCache();
  }

  updateContentSize(): void {
    this.scrollStrategy.onDataLengthChanged();
  }

  updateItemSize(index: number, value: number): void {
    this.scrollStrategy.updateItemSize(index, value);
  }
}
